import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUPER_ADMIN_EMAIL = 'captsaravanan18@gmail.com';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // The last user the server confirmed for this token, so the app can open at sea.
  const rememberUser = (u) => {
    try { localStorage.setItem('user', JSON.stringify(u)); } catch { /* storage full */ }
  };
  const rememberedUser = () => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      rememberUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // No response at all = no internet or server unreachable. That says nothing about
      // the login, so keep it and open with the last confirmed user. Only an answer from
      // the server (401 expired/revoked, 403 suspended, ...) ends the session.
      const cached = !error.response && rememberedUser();
      if (cached) {
        setUser(cached);
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    rememberUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, invite_token, company_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, invite_token, company_name });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    rememberUser(response.data.user);
    return response.data;
  };

  // Clears the session only. Unsent offline answers and photos stay on the device.
  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  const refreshUser = async () => {
    if (token) {
      await fetchCurrentUser();
    }
  };

  const isSuperAdmin = user?.role === 'SUPER_ADMIN' || user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase() || user?.is_admin_unlocked;
  const isManager = user?.role === 'MANAGER' || isSuperAdmin;
  const isSuperintendent = user?.role === 'SUPERINTENDENT';
  const isVesselUser = user?.role === 'VESSEL' && !isSuperAdmin;
  const isPending = user?.role === 'PENDING' || user?.role === 'PENDING_ADMIN';

  const isExpired = (() => {
    if (!user?.subscription_expiry && !user?.company_expiry) return false;
    const expiry = user?.company_expiry || user?.subscription_expiry;
    if (!expiry) return false;
    return new Date() > new Date(expiry);
  })();

  const vesselLimitReached = !isSuperAdmin && (user?.total_vessel_count ?? 0) >= (user?.company_vessel_limit ?? 1);

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, refreshUser, isSuperAdmin, isManager, isSuperintendent, isVesselUser, isPending, isExpired, vesselLimitReached }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
